import { Alert } from "components/elements";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { message, Modal } from "antd";
import { generateRandomString } from "utils/helper";
import * as services from "../services/employeeApi";
import { setIsLoading, setShiftList, setShiftOptions } from "../slices/employeeSlice";
import { useForm } from "antd/es/form/Form";

function toParams(params = {}) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    pagination: {
      page: pagination.page,
      size: pagination.pageSize,
    },
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

function useEmployeeEmploymentShift() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const isLoading = useSelector((state) => state.employee.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const shiftList = useSelector((state) => state.employee.shiftList);
  const [isModalOpen, setModalOpen] = useState(false);
  const [currentTimePlanShift, setCurrentTimePlanShift] = useState({});
  const [shiftDropDown, setShiftDropDown] = useState([]);
  const { shiftOptions } = useSelector(
    (state) => state.employee
  );
  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorter, setSorter] = useState([]);
  const { t, i18n } = useTranslation();

  // console.log("shiftList", shiftList);

  const getAttendanceShiftOptions = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getShiftOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      const modifiedOptions = response?.data?.map((opt) => ({
        ...opt,
        label: i18n.language === "en" ? opt.nameEn : opt.name,
        value: opt.id,
      }));
      setShiftDropDown(modifiedOptions);
    } catch (error) {
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, i18n.language]);

  const getShiftOptions = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getShiftOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
      });
      dispatch(setShiftOptions(response.data));
    } catch (error) {
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany]);

  const getTimePlanShiftPaging = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getTimeplanShiftPaging({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: id,
          actionId: currentEmployee,
          ...params.pagination,
          sortBy: params.sortBy || [],
          ...(params.endDate && {
            endDate: params.endDate.format("YYYY-MM-DD"),
          }),

        });
        dispatch(setShiftList(response.data?.content || []));
        setPagination({
          ...params.pagination,
          total: response.data?.totalItems,
        });
      } catch (error) {
        Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, id]
  );

  const createTimePlanShift = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.createTimePlanShift({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: id,
          actionId: currentEmployee,
          shiftId: params.shiftId,
          startDate: params?.shiftStartDate?.[0]?.format?.("YYYY-MM-DD") ?? params?.shiftStartDate?.format?.("YYYY-MM-DD"),
          endDate: params?.shiftStartDate?.[1]?.format?.("YYYY-MM-DD"),
        });
        await getTimePlanShiftPaging({
          pagination: {
            page: 0,
            size: 10,
          },
        });

        if (response?.data?.content?.details[0]?.key === "ErrorTH") {

          Alert({ type: "error", message: i18n.language === "en" ? response?.data?.content?.details[1]?.value : response?.data?.content?.details[0]?.value });
        } else {
          message.success(t("common.success"))
          setModalOpen(false);
        }
      } catch (error) {

        function contentDetail(params) {
          return error?.data?.status?.details?.map?.(o => {
            return <div>{o?.key} <br /> {o?.value}</div>
          })
        }
        Modal.error({ content: (contentDetail()), okText: t("confirm") })
        // Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch, getTimePlanShiftPaging, i18n.language, id]
  );

  const updateTimePlanShift = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.updateTimeplanShift({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: id,
          actionId: currentEmployee,
          shiftId: params?.shiftId,
          startDate: params?.startDate?.format("YYYY-MM-DD"),
          timePlanShiftId: params?.timePlanShiftId,
          endDate: params.endDate?.format("YYYY-MM-DD") ?? null,
        });
        await getTimePlanShiftPaging({
          pagination: {
            page: 0,
            size: 10,
          },
        });

        setModalOpen(false); setCurrentTimePlanShift({});
        message.success(t("common.success"))
      } catch (error) {
        function contentDetail(params) {
          return error?.data?.status?.details?.map?.(o => {
            return <div>{o?.key} <br /> {o?.value}</div>
          })
        }
        Modal.error({ content: (contentDetail()), okText: t("confirm") })
        // Alert({ type: "error", resultObject: error });
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch, getTimePlanShiftPaging, id]
  );

  const deleteTimePlanShift = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.deleteTimePlanShift({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: id,
          actionId: currentEmployee,
          timePlanShiftId: params,
        });
        await getTimePlanShiftPaging({
          pagination: {
            page: 0,
            size: 10,
          },
        });
        setCurrentTimePlanShift({});
        setModalOpen(false);
        message.success(t("common.success"))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch, getTimePlanShiftPaging, id]
  );

  const onSubmit = useCallback(
    async (values, form) => {
      // console.log("onSubmit", form);

      const modifiedValues = {
        ...values
      };
      await createTimePlanShift(modifiedValues);
      // setCurrentTimePlanShift({});
      console.log("log value :: ", values);
      form?.resetFields?.()

    },
    [updateTimePlanShift, createTimePlanShift]
  );

  const handleOnChange = useCallback(
    (tablePagination, _, tableSorter, __) => {
      setPagination(tablePagination);
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      setSorter(modifiedSorter);
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },
        sortBy: modifiedSorter,
      });
      getTimePlanShiftPaging(modifiedParams);
    },
    [getTimePlanShiftPaging]
  );

  const onEdit = (record) => {
    // console.log("record", record);
    setCurrentTimePlanShift(record);
    setModalOpen(true);
  };

  const onDelete = (record) => {
    deleteTimePlanShift(record);
  };

  const onCancel = useCallback(() => {
    // console.log("onCancel");
    setCurrentTimePlanShift({});
    setModalOpen(false);
  }, []);

  const onCreate = useCallback(() => {
    setModalOpen(true);
    setCurrentTimePlanShift({});
  }, []);

  const modifiedShiftOptions = useMemo(() => {
    const modifiedOptions = shiftOptions?.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    return modifiedOptions;
  }, [i18n.language, shiftOptions]);

  useEffect(() => {
    if (id && currentCompany) {
      getTimePlanShiftPaging({
        id: id,
        pagination: { page: 0, size: 10 },
      });
      getShiftOptions();
    }
  }, [currentCompany, id]);

  return {
    isLoading,
    shiftList,
    pagination,
    isModalOpen,
    currentTimePlanShift,
    shiftOptions: modifiedShiftOptions,
    shiftDropDown,
    getAttendanceShiftOptions,
    onChange: handleOnChange,
    onCreate,
    onEdit,
    onCancel,
    onSubmit,
    onDelete,
    getShiftOptions
  };
}

export default useEmployeeEmploymentShift;
